import { formatPrice, semanticJurisdiction } from '@/lib/helpers';
import {
  TService,
  useIncludableServices,
  useServicesFiltered,
} from '@/models/services';
import { Group, Stack, Table, Text } from '@mantine/core';
import { ReactNode, useEffect } from 'react';
import { PiPackageDuotone } from 'react-icons/pi';
import RichTooltip from '../common/RichTooltip';
import classes from './ServiceList.module.css';

export type ServiceListRenderer = (
  service: TService,
  onSelect: (service: TService) => void,
) => ReactNode;

export interface ServiceListProps {
  parentServiceId?: number;
  jurisdiction?: string;
  currency?: string;
  term?: string;
  exclude?: number[];
  cursor?: number;
  type?: string;
  minPrice?: number;
  maxPrice?: number;
  serviceFilter?: 'packages' | 'standalone';
  isAdditionalService?: boolean;
  strictJurisdiction?: boolean;
  onSelect: (service: TService) => void;
  itemRenderer?: ServiceListRenderer;
  onLoaded?: (total: number, cursor?: number) => void;
}

export default function ServiceList({
  parentServiceId,
  onSelect,
  term,
  jurisdiction,
  cursor,
  currency,
  type,
  minPrice,
  maxPrice,
  serviceFilter,
  isAdditionalService,
  strictJurisdiction,
  itemRenderer,
  onLoaded,
  exclude,
}: ServiceListProps) {
  const { data: includable, isLoading: isLoadingIncludableServices } =
    useIncludableServices(
      parentServiceId || 0,
      { term, jurisdiction, exclude, currency },
      cursor,
    );

  const { data: all, isLoading: isLoadingAllServices } = useServicesFiltered(
    {
      term,
      jurisdiction,
      strictJurisdiction,
      exclude,
      currency,
      type,
      minPrice,
      maxPrice,
      serviceFilter,
      isAdditionalService,
    },
    cursor,
    !parentServiceId,
  );

  const isLoading = isLoadingIncludableServices || isLoadingAllServices;

  const {
    services,
    cursor: nextCursor,
    total,
  } = includable || all || { total: 0, services: [] };

  useEffect(() => {
    if (!isLoading && onLoaded) {
      onLoaded(total, nextCursor || 0);
    }
  }, [isLoading, nextCursor, total, onLoaded]);

  if (isLoading) {
    return (
      <Text ta="center" py="xl">
        Loading services...
      </Text>
    );
  }

  if (services.length === 0) {
    return (
      <Text ta="center" py="xl">
        No services found
      </Text>
    );
  }

  const sorted = [...services].sort((a, b) => {
    if (a.children.length > 0 && b.children.length === 0) return -1;
    if (a.children.length === 0 && b.children.length > 0) return 1;
    return 0;
  });

  if (itemRenderer) {
    return <>{sorted.map((service) => itemRenderer(service, onSelect))}</>;
  }

  return (
    <Table highlightOnHover fz="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th style={{ width: 1, whiteSpace: 'nowrap' }}>
            Jurisdiction
          </Table.Th>
          <Table.Th style={{ width: 1, whiteSpace: 'nowrap' }}>Type</Table.Th>
          <Table.Th style={{ width: 1, whiteSpace: 'nowrap' }}>
            Industry
          </Table.Th>
          <Table.Th style={{ width: 1, whiteSpace: 'nowrap' }} ta="right">
            Price
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sorted.map((service) => (
          <Table.Tr
            key={service.id}
            onClick={() => onSelect(service)}
            className={classes.item}
          >
            <Table.Td>
              {service.children.length > 0 ? (
                <RichTooltip
                  content={
                    <Stack gap={3}>
                      {service.children.map((child) => (
                        <Text size="xs" key={child.id} c="dimmed">
                          - {child.name}
                        </Text>
                      ))}
                    </Stack>
                  }
                >
                  <Group gap={2} wrap="nowrap">
                    <PiPackageDuotone size={14} />
                    <Text size="xs" lineClamp={1}>
                      {service.name}
                    </Text>
                  </Group>
                </RichTooltip>
              ) : (
                <Text size="xs" lineClamp={1}>
                  {service.name}
                </Text>
              )}
            </Table.Td>
            <Table.Td style={{ whiteSpace: 'nowrap' }}>
              <Text size="xs" c="dimmed">
                {service.jurisdictions?.length
                  ? service.jurisdictions
                      .map((j) => semanticJurisdiction(j))
                      .join(', ')
                  : 'Any'}
              </Text>
            </Table.Td>
            <Table.Td style={{ whiteSpace: 'nowrap' }}>
              <Text size="xs" c="dimmed">
                {service.type}
              </Text>
            </Table.Td>
            <Table.Td style={{ whiteSpace: 'nowrap' }}>
              <Text size="xs" c="dimmed">
                {service.industry}
              </Text>
            </Table.Td>
            <Table.Td style={{ whiteSpace: 'nowrap' }} ta="right">
              <Text size="xs">
                {formatPrice(service.price, service.currency)}
              </Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}
